import { useEffect, useState } from "react";
import { Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import BottomTabBar from "@/components/layout/BottomTabBar";
import TopHeader from "@/components/layout/TopHeader";
import DesktopSidebar from "@/components/layout/DesktopSidebar";
import DesktopPageHeader from "@/components/layout/DesktopPageHeader";
import FAB from "@/components/layout/FAB";
import CreateDialog from "@/components/CreateDialog";
import PushPermissionPrompt from "@/components/PushPermissionPrompt";
import InstallAppPrompt from "@/components/InstallAppPrompt";
import FloatingCoach from "@/components/FloatingCoach";
import Dashboard from "@/pages/Dashboard";
import DesktopDashboard from "@/pages/DesktopDashboard";
import Agenda from "@/pages/Agenda";
import Chantiers from "@/pages/Chantiers";
import ChantierDetail from "@/pages/ChantierDetail";
import Documents from "@/pages/Documents";
import Reglages from "@/pages/Reglages";
import AIAssistant from "@/pages/AIAssistant";
import AdminConsole from "@/pages/AdminConsole";
import GlobalFeed from "@/pages/GlobalFeed";
import EquipmentDetail from "@/pages/EquipmentDetail";
import LeaveRequests from "@/pages/LeaveRequests";
import AlertesPanel from "@/pages/AlertesPanel";
import AdminTraces from "@/pages/AdminTraces";
import HeuresDeplacements from "@/pages/HeuresDeplacements";
import PayrollReport from "@/pages/PayrollReport";
import MyVehicle from "@/pages/MyVehicle";
import Messages from "@/pages/Messages";
import { useAuth } from "@/context/AuthContext";
// GPS is intentionally NEVER captured from the browser. The mobile app
// (BKPRO Pro Tech, Expo) is the sole source of truth for team positions —
// see `/app/mobile/lib/locationBeacon.ts`. Web only *reads* positions.
import useIsDesktop from "@/hooks/useIsDesktop";

const TAB_TITLES = {
  "/": "Tableau de bord",
  "/agenda": "Agenda",
  "/chantiers": "Chantiers",
  "/documents": "Documents",
  "/reglages": "Réglages",
  "/ia": "Assistant IA",
  "/admin": "Console gérant",
  "/admin/traces": "Tracés journaliers",
  "/heures": "Heures & Déplacements",
  "/payroll": "Paniers & Trajets",
  "/rh": "Congés",
  "/fil": "Fil d'actualité",
  "/alertes": "Alertes & SAV",
  "/vehicule": "Mon véhicule",
  "/messages": "Messages",
};

export default function AppShell() {
  const [createOpen, setCreateOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDesktop = useIsDesktop();

  // GPS captation from the browser is DISABLED (Karim 2026-02-19). Mobile app
  // is the only source. Do NOT add `navigator.geolocation.*` calls here.

  let title = TAB_TITLES[location.pathname] || "";
  if (location.pathname.startsWith("/chantiers/") && location.pathname !== "/chantiers") {
    title = "Détail chantier";
  }

  // On desktop, the "Accueil" landing page is reserved for direction
  // (admin/compta/associate). Technicians never see it and land on the Agenda.
  const isDirection = user?.role === "admin" || user?.role === "compta" || user?.role === "associate";
  const homeElement = isDesktop
    ? (isDirection ? <DesktopDashboard /> : <Navigate to="/agenda" replace />)
    : <Dashboard onCreate={() => setCreateOpen(true)} />;

  const routes = (
    <Routes>
      <Route
        path="/"
        element={homeElement}
      />
      <Route path="/agenda" element={<Agenda />} />
      <Route path="/chantiers" element={<Chantiers />} />
      <Route path="/chantiers/:id" element={<ChantierDetail />} />
      <Route path="/documents" element={<Documents />} />
      <Route path="/reglages" element={<Reglages />} />
      <Route path="/ia" element={<AIAssistant />} />
      <Route path="/fil" element={<GlobalFeed />} />
      <Route path="/admin" element={<AdminConsole />} />
      <Route path="/admin/traces" element={<AdminTraces />} />
      <Route path="/heures" element={<HeuresDeplacements />} />
      <Route path="/payroll" element={<PayrollReport />} />
      <Route path="/rh" element={<LeaveRequests />} />
      <Route path="/alertes" element={<AlertesPanel />} />
      <Route path="/vehicule" element={<MyVehicle />} />
      <Route path="/messages" element={<Messages />} />
      <Route path="/messages/:peerId" element={<Messages />} />
      <Route path="/equipment/:id" element={<EquipmentDetail />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );

  if (isDesktop) {
    return (
      <div className="min-h-screen w-full bg-[#0A0A0A] text-white flex" data-testid="app-shell-desktop">
        <DesktopSidebar onCreate={() => setCreateOpen(true)} />
        <div className="flex-1 min-w-0 flex flex-col">
          <DesktopPageHeader title={title} subtitle="BKflow" />
          <main className="flex-1 overflow-y-auto" data-testid="app-main">
            <div className="max-w-[1600px] mx-auto">
              {routes}
            </div>
          </main>
        </div>
        <CreateDialog open={createOpen} onOpenChange={setCreateOpen} />
        <PushPermissionPrompt />
        <InstallAppPrompt />
        {location.pathname !== "/ia" && <FloatingCoach />}
      </div>
    );
  }

  // Mobile — unchanged
  return (
    <div className="min-h-screen w-full bg-[#0A0A0A] text-white flex flex-col max-w-2xl mx-auto relative" data-testid="app-shell-mobile">
      <TopHeader title={title} />
      <main className="flex-1 overflow-y-auto pb-28" data-testid="app-main">
        {routes}
      </main>
      <FAB onClick={() => setCreateOpen(true)} />
      <BottomTabBar />
      <CreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      <PushPermissionPrompt />
      <InstallAppPrompt />
      {location.pathname !== "/ia" && <FloatingCoach />}
    </div>
  );
}
